import { Box, CircularProgress, Grid } from '@mui/material';
import MDTypography from 'components/MDTypography/index';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCurrentPage,
  fetchUsersDetails,
  fetchUserBuildingsDetails,
} from '../../stores/sensor/sensorStore';
import { PermissionUserCard } from './PermissionUserCard';

export const PermissionUsersList = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const { users, loading: usersLoading } = useSelector(state => state.users);
  const { buildings_details: buildings, loading: buildingsLoading } = useSelector(
    state => state.sensors,
  );
  const loading = usersLoading || buildingsLoading;

  useEffect(() => {
    dispatch(setCurrentPage('permissions-list'));
    dispatch(fetchUsersDetails());
    dispatch(fetchUserBuildingsDetails());
  }, [dispatch]);

  // Enhance users data with building permission information
  const enhancedUsers = useMemo(() => {
    if (!users || !buildings) return [];

    return users.map(user => {
      // Get assigned building count and names from user's building_permissions
      const assignedBuildingsCount = user.building_permissions
        ? user.building_permissions.length
        : 0;
      const buildingNames = user.building_names || [];

      // Count areas across assigned buildings
      const totalAreas = user.building_permissions.reduce(
        (sum, x) => sum + (Array.isArray(x.floor_and_area_ids) ? x.floor_and_area_ids.length : 0),
        0,
      );

      // Get customer names from buildings where this distributor has permissions
      let customerNames = [];
      if (user.role === 'd' && user.building_permissions) {
        const customerSet = new Set();
        user.building_permissions.forEach(permission => {
          const building = buildings.find(b => b.id === permission.building_id);
          if (building?.users) {
            Object.values(building.users).forEach(buildingUser => {
              if (buildingUser.role === 'c' && buildingUser.id !== user.id) {
                customerSet.add(buildingUser.username);
              }
            });
          }
        });
        customerNames = Array.from(customerSet);
      }

      return {
        ...user,
        assignedBuildingsCount,
        buildingNames,
        totalAreas,
        customerNames,
      };
    });
  }, [users, buildings]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <MDTypography variant="h4" sx={{ color: 'black', mb: 1, py: 1, px: 0.2 }}>
        Users
      </MDTypography>

      <Box id="permission-users-content" sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box sx={{ width: '100%' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && enhancedUsers && enhancedUsers.length > 0 && (
            <Grid
              className="permissionUsersListContainer"
              sx={{
                display: 'flex',
                alignItems: 'start',
                height: 'fit-content',
                maxHeight: '71vh',
                overflow: 'auto',
                width: '88vw',
                flexWrap: 'wrap',
                overflowX: 'hidden',
                p: '1rem',
              }}
              margin={0}>
              {enhancedUsers
                .filter(u => u.role !== 's')
                .map(user => (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={4}
                    xl={3}
                    key={user.id}
                    sx={{
                      lineHeight: 0,
                      position: 'relative',
                      marginBottom: '1rem',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: { xs: 'center', sm: 'start' },
                    }}>
                    <PermissionUserCard
                      className="permissionUserCard"
                      id={user.id}
                      username={user.username}
                      role={user.role}
                      roleDisplay={user.role_display}
                      buildingNames={user.buildingNames}
                      totalAreas={user.role === 's' ? 'All' : user.totalAreas}
                      customerNames={user.customerNames}
                      // cardClickInfo="Click to manage"
                    />
                  </Grid>
                ))}
            </Grid>
          )}

          {!loading && enhancedUsers && enhancedUsers.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MDTypography variant="h6" color="text">
                No users to display
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
                Contact your administrator for more details
              </MDTypography>
            </Box>
          )}

          {!loading && !enhancedUsers && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MDTypography variant="h6" color="error">
                Failed to load permission data
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
                Please try refreshing the page
              </MDTypography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
